import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton, Paper
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ForestIcon from '@mui/icons-material/Forest';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';
const PURPLE = '#6a1b9a';

const HDR_SX = {
  fontWeight: 700,
  color: '#fff',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${PURPLE} 0%, #4a148c 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)',
  py: 1.2,
  px: 0.8,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const CELL_SX = { py: 0.9, px: 0.8, fontSize: '0.77rem' };

function fmtNum(v, d = 2) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function isGrandTotal(row) {
  return String(row.FOREST || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

// ─── Detail Table ─────────────────────────────────────────────────────────────
function DetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;

  const cols = [
    { label: '#',              key: '__idx',            align: 'center' },
    { label: 'CHALAN NO',      key: 'CHALAN_NO',        align: 'center' },
    { label: 'LOG NO',         key: 'LOG_NO',           align: 'center' },
    { label: 'FOREST',         key: 'FOREST',           align: 'center' },
    { label: 'ESSENCE',        key: 'ESSENCE',          align: 'center' },
    { label: 'QUALITY',        key: 'QUALITY',          align: 'center' },
    { label: 'LENGTH (m)',     key: 'LENGTH',           align: 'center', fmt: v => fmtNum(v, 2) },
    { label: 'CBM (BORD.)',    key: 'CBM_BORDEREAU',    align: 'center', fmt: v => fmtNum(v, 3) },
    { label: 'CBM (ACTUAL)',   key: 'BORD_ACTUAL_CBM',  align: 'center', fmt: v => fmtNum(v, 3) },
    { label: 'RECEIVING DATE', key: 'RECEIVING_DATE',   align: 'center', fmt: v => v ? String(v).substring(0, 10) : '—' },
    { label: 'STATUS',         key: 'CURRENT_STATUS',   align: 'center' },
    { label: 'COMPANY',        key: 'COMPANY',          align: 'center' },
    { label: 'COST/CBM',       key: 'Cost_per_CBM',     align: 'center', fmt: v => fmtNum(v, 2) },
    { label: 'STOCK VALUE',    key: 'STOCK_VALUE',      align: 'center', fmt: v => fmtNum(v, 2) },
  ];

  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#faf5ff',
              '&:hover': { backgroundColor: '#ede7f6' },
            }}>
              {cols.map(col => {
                const val = col.key === '__idx'
                  ? i + 1
                  : col.fmt
                    ? col.fmt(row[col.key])
                    : (row[col.key] ?? '—');
                return (
                  <TableCell key={col.label} sx={{ ...CELL_SX, textAlign: col.align }}>
                    {val}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary By Forest Table ──────────────────────────────────────────────────
function SummaryForestTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;

  const cols = [
    { label: 'FOREST',         key: 'FOREST',          align: 'center' },
    { label: 'ESSENCE',        key: 'ESSENCE',         align: 'center' },
    { label: 'LOG COUNT',      key: 'LOG_COUNT',        align: 'center', fmt: v => fmtNum(v, 0) },
    { label: 'CBM (BORD.)',    key: 'CBM_BORDEREAU',   align: 'center', fmt: v => fmtNum(v, 3) },
    { label: 'CBM (CALC.)',    key: 'CBM_CALCULATION', align: 'center', fmt: v => fmtNum(v, 3) },
    { label: 'CBM (ACTUAL)',   key: 'BORD_ACTUAL_CBM', align: 'center', fmt: v => fmtNum(v, 3) },
    { label: 'TOTAL COST',     key: 'TOTAL_COST',      align: 'center', fmt: v => fmtNum(v, 2) },
    { label: 'AVG COST/CBM',   key: '__avg',           align: 'center',
      computeRow: (row) => {
        const cost = parseFloat(row.TOTAL_COST);
        const cbm  = parseFloat(row.CBM_BORDEREAU);
        return (cbm > 0 && !isNaN(cost)) ? fmtNum(cost / cbm, 2) : '—';
      }
    },
  ];

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 750 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#d1fae5' : (i % 2 === 0 ? '#fff' : '#faf5ff'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {cols.map(col => {
                  const val = col.computeRow
                    ? col.computeRow(row)
                    : col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      textAlign: col.align,
                      fontWeight: grand ? 700 : 500,
                      color: grand ? '#14532d' : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary By Forest + Quality Table ───────────────────────────────────────
function SummaryForestQualityTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;

  const cols = [
    { label: 'FOREST',         key: 'FOREST',          align: 'center' },
    { label: 'ESSENCE',        key: 'ESSENCE',         align: 'center' },
    { label: 'QUALITY',        key: 'QUALITY',         align: 'center' },
    { label: 'LOG COUNT',      key: 'LOG_COUNT',        align: 'center', fmt: v => fmtNum(v, 0) },
    { label: 'CBM (BORD.)',    key: 'CBM_BORDEREAU',   align: 'center', fmt: v => fmtNum(v, 3) },
    { label: 'CBM (CALC.)',    key: 'CBM_CALCULATION', align: 'center', fmt: v => fmtNum(v, 3) },
    { label: 'CBM (ACTUAL)',   key: 'BORD_ACTUAL_CBM', align: 'center', fmt: v => fmtNum(v, 3) },
    { label: 'COST/CBM',       key: 'TOTAL_COST',      align: 'center', fmt: v => fmtNum(v, 2) },
    { label: 'AVG COST/CBM',   key: '__avg',           align: 'center',
      computeRow: (row) => {
        const cost = parseFloat(row.TOTAL_COST);
        const cbm  = parseFloat(row.CBM_BORDEREAU);
        return (cbm > 0 && !isNaN(cost)) ? fmtNum(cost / cbm, 2) : '—';
      }
    },
  ];

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 850 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#d1fae5' : (i % 2 === 0 ? '#fff' : '#faf5ff'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {cols.map(col => {
                  const val = col.computeRow
                    ? col.computeRow(row)
                    : col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      textAlign: col.align,
                      fontWeight: grand ? 700 : 500,
                      color: grand ? '#14532d' : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Stat chip ────────────────────────────────────────────────────────────────
function StatChip({ label, value, color = PURPLE }) {
  return (
    <Paper elevation={0} sx={{
      px: 2, py: 1.5, borderRadius: 2,
      border: `1.5px solid ${color}30`,
      background: `${color}08`,
      textAlign: 'center',
      minWidth: 110,
    }}>
      <Typography sx={{ fontSize: '0.68rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color }}>
        {value}
      </Typography>
    </Paper>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────
const TAB_LABELS = ['DETAIL', 'SUMMARY BY FOREST', 'SUMMARY BY FOREST + QUALITY'];

const LogBoardeauReportView = ({ onBackClick, user }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate,   setToDate]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [data,     setData]     = useState(null);
  const [tab,      setTab]      = useState(0);

  const fetchData = async () => {
    if (!fromDate || !toDate) { setError('Please select both From and To dates'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/boardeaureport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date range.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    [
      { label: 'Detail',                   rows: data[0] },
      { label: 'Summary By Forest',        rows: data[1] },
      { label: 'Summary By Forest+Quality', rows: data[2] },
    ].forEach(({ label, rows }) => {
      if (rows?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), label);
    });
    const fd = fromDate.format('YYYY-MM-DD');
    const td = toDate.format('YYYY-MM-DD');
    XLSX.writeFile(wb, `LogBoardeau_${fd}_to_${td}.xlsx`);
  };

  // Quick stats from RS[1] grand total row
  const grandTotal = data?.[1]?.find(r => isGrandTotal(r));

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(106,27,154,0.1)', border: '1px solid #f3e5f5' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: PURPLE }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, #4a148c 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <ForestIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: PURPLE }}>Log Boardeau (Inward) Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Log inward details, CBM, costs — by date range</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={setToDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <Button
                variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{
                  minWidth: 140,
                  backgroundColor: PURPLE,
                  '&:hover': { backgroundColor: '#4a148c' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>

          {/* Quick stats */}
          {grandTotal && (
            <Box display="flex" flexWrap="wrap" gap={1.5} mt={2}>
              <StatChip label="Total Logs"    value={fmtNum(grandTotal.LOG_COUNT, 0)} />
              <StatChip label="CBM Bordereau" value={fmtNum(grandTotal.CBM_BORDEREAU, 3)} />
              <StatChip label="CBM Actual"    value={fmtNum(grandTotal.BORD_ACTUAL_CBM, 3)} color={GREEN} />
              <StatChip label="Total Cost"    value={fmtNum(grandTotal.TOTAL_COST, 2)} color="#c62828" />
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: PURPLE }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f3e5f5' }}>
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab} onChange={(_, v) => setTab(v)}
              textColor="inherit" variant="scrollable" scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: PURPLE },
                '& .MuiTabs-indicator': { backgroundColor: PURPLE },
              }}
            >
              {TAB_LABELS.map((l, i) => <Tab key={i} label={l} />)}
            </Tabs>
            <Button
              size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: PURPLE, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}
            >
              Excel
            </Button>
          </Box>

          {tab === 0 && <DetailTable               rows={data[0]} />}
          {tab === 1 && <SummaryForestTable         rows={data[1]} />}
          {tab === 2 && <SummaryForestQualityTable  rows={data[2]} />}
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Log Boardeau Report to WhatsApp"
            apiUrl={
              fromDate && toDate
                ? `${API_URL}/api/logboardeau/send?from_date=${fromDate.format('YYYY-MM-DD')}&to_date=${toDate.format('YYYY-MM-DD')}`
                : null
            }
            disabled={!fromDate || !toDate}
          />
        </Box>
      )}
    </Container>
  );
};

export default LogBoardeauReportView;
